import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from 'axios';
import config from 'src/config';
import { useAuth } from 'src/util/authenticationProvider';

const quillModules = {
  toolbar: [
    [{ 'font': [] }, { 'size': [] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'script': 'sub'}, { 'script': 'super' }],
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
    [{ 'align': [] }],
    ['blockquote', 'code-block'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'indent': '-1'}, { 'indent': '+1' }],
    ['link', 'image', 'video'],
    ['clean']
  ]
};

export default function Campaigns() {
  const { t } = useTranslation();
  const authContext = useAuth();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [locations, setLocations] = useState<any[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<number[]>([]);
  const [participants, setParticipants] = useState<any[]>([]);
  const [loadingParticipants, setLoadingParticipants] = useState(true);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [leadLists, setLeadLists] = useState<any[]>([]);
  const [selectedLeadLists, setSelectedLeadLists] = useState<string[]>([]);

  useEffect(() => {
    const controller = new AbortController();
    axios
      .get(`${config.apiUrl}/api/location/all`, {
        headers: {
          Authorization: `Bearer ${authContext.tokens?.accessToken ?? ''}`,
        },
        signal: controller.signal,
      })
      .then(res => {
        setLocations(Array.isArray(res.data) ? res.data : res.data.content || []);
      })
      .catch(err => {
        if (axios.isCancel(err)) {
          // Request was cancelled, do nothing
        }
      });
    return () => {
      controller.abort();
    };
  }, [authContext.tokens?.accessToken]);

  useEffect(() => {
    const controller = new AbortController();
    setLoadingParticipants(true);
    axios
      .get(`${config.apiUrl}/api/lottery/all/participants`, {
        headers: {
          Authorization: `Bearer ${authContext.tokens?.accessToken ?? ''}`,
        },
        signal: controller.signal,
      })
      .then(res => {
        setParticipants(Array.isArray(res.data) ? res.data : res.data.content || []);
        setLoadingParticipants(false);
      })
      .catch(err => {
        if (axios.isCancel(err)) {
          // Request was cancelled, do nothing
        }
        setLoadingParticipants(false);
      });
    return () => {
      controller.abort();
    };
  }, [authContext.tokens?.accessToken]);

  useEffect(() => {
    const controller = new AbortController();
    axios
      .get(`${config.apiUrl}/api/leadlists/all`, {
        headers: {
          Authorization: `Bearer ${authContext.tokens?.accessToken ?? ''}`,
        },
        signal: controller.signal,
      })
      .then(res => {
        const payload = res?.data?.content ?? res?.data?.data ?? res?.data;
        const arr = Array.isArray(payload) ? payload : [];
        setLeadLists(arr.map((list: any) => ({ ...list, id: String(list.id) })));
      })
      .catch(err => {
        if (axios.isCancel(err)) {
          // ignore cancel
        }
      });
    return () => controller.abort();
  }, [authContext.tokens?.accessToken]);

  useEffect(() => {
    const fromLocations = participants
      .filter((p) => selectedLocations.length === 0 ? false : selectedLocations.includes(p.location?.id))
      .map((p) => p.email)
      .filter(Boolean);

    const fromLeadLists = leadLists
      .filter((list) => selectedLeadLists.includes(list.id))
      .flatMap((list) => (Array.isArray(list.targets) ? list.targets : []))
      .map((target: any) => target?.email || target?.contactEmail || target?.address)
      .filter(Boolean);

    const combined = Array.from(new Set([...(fromLocations as string[]), ...(fromLeadLists as string[])]));
    setRecipients(combined);
  }, [selectedLocations, participants, leadLists, selectedLeadLists]);

  const handleLocationChange = (event: any) => {
    const value = event.target.value;
    if (value.includes("all")) {
      // Select all locations
      if (selectedLocations.length === locations.length) {
        setSelectedLocations([]);
      } else {
        setSelectedLocations(locations.map(loc => loc.id));
      }
    } else {
      setSelectedLocations(
        typeof value === 'string'
          ? value.split(',').map(Number)
          : value as number[]
      );
    }
  };

  const handleLeadListChange = (event: any) => {
    const value = event.target.value;
    if (value.includes('all')) {
      if (selectedLeadLists.length === leadLists.length) {
        setSelectedLeadLists([]);
      } else {
        setSelectedLeadLists(leadLists.map((list) => list.id));
      }
    } else {
      setSelectedLeadLists(typeof value === 'string' ? value.split(',') : value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGeneralError(null);
    setSuccessMessage(null);
    if (recipients.length === 0) {
      setLocationError(t('campaigns.selectRecipientsError', 'Please select at least one recipient.'));
      return;
    }
    setLocationError(null);
    try {
      await axios.post(
        `${config.apiUrl}/api/notifications/send-email`,
        {
          recipients,
          subject,
          body,
        },
        {
          headers: {
            Authorization: `Bearer ${authContext.tokens?.accessToken ?? ''}`,
          },
        }
      );
      // Reset form on success
      setSubject('');
      setBody('');
      setSelectedLocations([]);
      setSelectedLeadLists([]);
      setRecipients([]);
      setGeneralError(null);
      setSuccessMessage(t('campaigns.sendSuccess', 'Campaign sent successfully!'));
    } catch (error: any) {
      setGeneralError(
        error?.response?.data?.message ||
        error?.message ||
        t('campaigns.sendError', 'Failed to send campaign.')
      );
      setSuccessMessage(null);
    }
  };

  return (
    <Paper sx={{ p: 4, maxWidth: 1000, mx: 'auto', mt: 6 }}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Typography variant="h5" gutterBottom>
            {t('campaigns.title')}
          </Typography>
          <Box component="form" onSubmit={handleSubmit}>
            {successMessage && (
              <Typography color="success.main" variant="body2" sx={{ mb: 2 }}>
                {successMessage}
              </Typography>
            )}
            {generalError && (
              <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                {generalError}
              </Typography>
            )}
            <TextField
              label={t('campaigns.subject')}
              value={subject}
              onChange={e => setSubject(e.target.value)}
              fullWidth
              required
              sx={{ mb: 3 }}
            />
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              {t('campaigns.body')}
            </Typography>
            <ReactQuill
              value={body}
              onChange={setBody}
              theme="snow"
              modules={quillModules}
              style={{ marginBottom: 75, minHeight: 300, height: 300 }}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!subject || !body || loadingParticipants}
            >
              {t('campaigns.send')}
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('campaigns.locationSelect')}
          </Typography>
          <FormControl fullWidth>
            <InputLabel id="location-select-label">{t('campaigns.locations')}</InputLabel>
            <Select
              labelId="location-select-label"
              multiple
              value={selectedLocations}
              label={t('campaigns.locations')}
              onChange={handleLocationChange}
              renderValue={(selected) =>
                locations
                  .filter(loc => selected.includes(loc.id))
                  .map(loc => loc.name)
                  .join(', ')
              }
            >
              {/* Select/Unselect All Checkbox */}
              <MenuItem value="all">
                <Checkbox
                  checked={
                    locations.length > 0 &&
                    selectedLocations.length === locations.length
                  }
                  indeterminate={
                    selectedLocations.length > 0 &&
                    selectedLocations.length < locations.length
                  }
                  onChange={e => {
                    if (e.target.checked) {
                      setSelectedLocations(locations.map(loc => loc.id));
                    } else {
                      setSelectedLocations([]);
                    }
                  }}
                />
                <Typography variant="body2">
                  {t('campaigns.selectAllLocations')}
                </Typography>
              </MenuItem>
              {locations.map(loc => (
                <MenuItem key={loc.id} value={loc.id}>
                  <Checkbox checked={selectedLocations.indexOf(loc.id) > -1} />
                  {loc.name}
                </MenuItem>
              ))}
            </Select>
            {locationError && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {locationError}
              </Typography>
            )}
          </FormControl>
          <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>
            {t('campaigns.leadLists', 'Lead lists')}
          </Typography>
          <FormControl fullWidth>
            <InputLabel id="leadlist-select-label">{t('campaigns.leadLists', 'Lead lists')}</InputLabel>
            <Select
              labelId="leadlist-select-label"
              multiple
              value={selectedLeadLists}
              label={t('campaigns.leadLists', 'Lead lists')}
              onChange={handleLeadListChange}
              renderValue={(selected) =>
                leadLists
                  .filter(list => selected.includes(list.id))
                  .map(list => list.name)
                  .join(', ')
              }
            >
              <MenuItem value="all">
                <Checkbox
                  checked={leadLists.length > 0 && selectedLeadLists.length === leadLists.length}
                  indeterminate={selectedLeadLists.length > 0 && selectedLeadLists.length < leadLists.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedLeadLists(leadLists.map((list) => list.id));
                    } else {
                      setSelectedLeadLists([]);
                    }
                  }}
                />
                <Typography variant="body2">
                  {t('campaigns.selectAllLeadLists', 'Select all lead lists')}
                </Typography>
              </MenuItem>
              {leadLists.map((list) => (
                <MenuItem key={list.id} value={list.id}>
                  <Checkbox checked={selectedLeadLists.indexOf(list.id) > -1} />
                  {list.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Loading animation for participants */}
          {loadingParticipants && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {/* Participants section is now hidden */}
        </Grid>
      </Grid>
    </Paper>
  );
}
